import { Booking } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface DeleteBookingsConfirmDialogProps {
  open: boolean;
  /** The multi-selection to delete. */
  bookings: Booking[];
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirms deleting a Ctrl/Cmd-click multi-selection of bookings in one action. */
export function DeleteBookingsConfirmDialog({
  open,
  bookings,
  onConfirm,
  onCancel,
}: DeleteBookingsConfirmDialogProps) {
  if (bookings.length === 0) {
    return null;
  }

  const recurringCount = bookings.filter(booking => !!booking.recurrenceGroupId).length;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Delete {bookings.length} Bookings
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the{' '}
            <span className="font-semibold text-foreground">{bookings.length}</span>
            {' '}selected bookings? This action cannot be undone.
            {recurringCount > 0 && (
              <>
                {' '}Only the selected occurrence
                {recurringCount === 1 ? '' : 's'} of recurring bookings
                {recurringCount === 1 ? ' is' : ' are'} deleted — later occurrences are kept.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Delete {bookings.length} Bookings
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
